import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import CommonTable, { type CommonTableProps } from '../common/CommonTable';
import { TABLE_STYLES } from '../../constants/tableStyles';
import { SEARCE_COLORS } from '../../theme';
import type { DataTableColumnDef } from './dataTableTypes';
import { renderDataTableCell } from './renderCell';
import type { TableId } from '../../constants/tableTotalsConfig';
import { computeTableTotals } from '../../utils/tableTotals';
import { TableTotalsFooterRow } from './TableTotalsFooterRow';

export type DataTableBodyRowContext<TData> = { row: TData; rowIndex: number };

export type DataTableProps<TData> = Omit<CommonTableProps, 'children'> & {
  columns: DataTableColumnDef<TData>[];
  /** Current page rows (parent handles filter/sort/pagination). */
  data: TData[];
  getRowId: (row: TData, index: number) => string;
  onRowClick?: (row: TData) => void;
  /** Merged into `<Table sx={...}>` */
  tableSx?: SxProps<Theme>;
  /** When true, skip the default left/right border reset on body/header cells (e.g. bench tables). */
  disableDefaultTableCellBorderReset?: boolean;
  /** Base styles for every header cell (e.g. utilization primary sticky header). */
  headerCellBaseSx?: SxProps<Theme>;
  /** `<TableRow>` wrapping headers */
  headerRowSx?: SxProps<Theme>;
  /** Default body row (merged with hover when `onRowClick` or `rowHover` is set) */
  bodyRowSx?: SxProps<Theme>;
  /** Per-row body styles (e.g. zebra striping); preferred over a function-typed `SxProps`. */
  resolveBodyRowSx?: (ctx: DataTableBodyRowContext<TData>) => SxProps<Theme>;
  /** MUI row hover without `onRowClick` (e.g. bench list). */
  rowHover?: boolean;
  /**
   * When set, replaces the default `[TABLE_STYLES.cell, { py: 0.75 }]` base for body cells
   * (still merged before column `meta.cellSx`).
   */
  bodyCellBaseSx?: SxProps<Theme>;
  /** When true, match embedded utilization tables (no thead radius, flush borders). */
  embedded?: boolean;
  /** Footer totals from `src/constants/tableTotalsConfig.ts` (uses full `data` array). */
  tableId?: TableId;
};

const defaultHeaderCellBaseSx: SxProps<Theme> = {
  ...TABLE_STYLES.cellHeader,
  borderBottom: `1px solid ${SEARCE_COLORS.border}`,
  backgroundColor: SEARCE_COLORS.neutral_100,
  color: SEARCE_COLORS.textPrimary,
  fontWeight: 600,
};

/**
 * Design-system table shell (CommonTable) + column-driven head/body.
 * Sorting/filtering stay in the parent; pass pre-sliced `data`.
 */
function DataTable<TData>({
  columns,
  data,
  getRowId,
  onRowClick,
  tableSx,
  disableDefaultTableCellBorderReset = false,
  headerCellBaseSx = defaultHeaderCellBaseSx,
  headerRowSx,
  bodyRowSx,
  resolveBodyRowSx,
  rowHover = false,
  bodyCellBaseSx,
  embedded = false,
  tableId,
  ...commonTableProps
}: DataTableProps<TData>): React.ReactElement {
  const headerBase = headerCellBaseSx;
  const footerTotals = React.useMemo(() => {
    if (!tableId || data.length === 0) return null;
    return computeTableTotals(
      tableId,
      data as Record<string, unknown>[],
      columns.map((c) => c.id),
    );
  }, [tableId, data, columns]);
  const defaultBodyCellStack: SxProps<Theme>[] = [TABLE_STYLES.cell as any, { py: 0.75 }];
  const bodyCellStack: SxProps<Theme>[] =
    bodyCellBaseSx != null ? [bodyCellBaseSx as any] : defaultBodyCellStack;

  return (
    <CommonTable {...commonTableProps}>
      <Table
        stickyHeader
        size={TABLE_STYLES.tableSize}
        sx={
          [
            disableDefaultTableCellBorderReset
              ? { width: '100%' }
              : {
                  width: '100%',
                  '& .MuiTableCell-root': {
                    borderRight: 'none',
                    borderLeft: 'none',
                  },
                },
            {
              '& thead .MuiTableRow-root': { display: 'table-row' },
              '& thead .MuiTableCell-root': {
                display: 'table-cell',
                ...(embedded ? { borderRadius: 0 } : {}),
              },
            },
            ...(tableSx != null ? [tableSx as any] : []),
          ] as SxProps<Theme>
        }
      >
        <TableHead>
          <TableRow sx={headerRowSx}>
            {columns.map((col) => {
              const m = col.meta;
              const headerContent =
                typeof col.header === 'function' ? col.header({ column: col }) : col.header;
              const hp = m?.headerCellProps;
              const { sx: headerPropsSx, ...headerRest } = hp ?? {};
              return (
                <TableCell
                  key={col.id}
                  align={m?.align}
                  colSpan={m?.colSpan}
                  {...headerRest}
                  sx={[
                    headerBase as any,
                    m?.width != null ? { width: m.width, minWidth: m.minWidth ?? m.width, maxWidth: m.maxWidth ?? m.width } : {},
                    m?.minWidth != null && m?.width == null ? { minWidth: m.minWidth } : {},
                    m?.maxWidth != null && m?.width == null ? { maxWidth: m.maxWidth } : {},
                    (m?.headerCellSx as any) ?? {},
                    ...(headerPropsSx != null ? [headerPropsSx as any] : []),
                  ]}
                >
                  {headerContent}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, rowIndex) => {
            const resolvedBodyRowSx = resolveBodyRowSx?.({ row, rowIndex });
            return (
            <TableRow
              key={getRowId(row, rowIndex)}
              hover={Boolean(onRowClick) || rowHover}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              sx={[
                TABLE_STYLES.tableRowHover as any,
                onRowClick ? { cursor: 'pointer' } : {},
                { '& .MuiTableCell-root': { verticalAlign: 'middle' } },
                ...(bodyRowSx != null ? [bodyRowSx as any] : []),
                ...(resolvedBodyRowSx != null ? [resolvedBodyRowSx as any] : []),
              ]}
            >
              {columns.map((col) => {
                const m = col.meta;
                return (
                  <TableCell
                    key={col.id}
                    align={m?.align}
                    colSpan={m?.colSpan}
                    sx={[
                      ...bodyCellStack.map((s) => s as any),
                      m?.width != null ? { width: m.width, minWidth: m.minWidth ?? m.width, maxWidth: m.maxWidth ?? m.width } : {},
                      m?.minWidth != null && m?.width == null ? { minWidth: m.minWidth } : {},
                      m?.maxWidth != null && m?.width == null ? { maxWidth: m.maxWidth } : {},
                      (m?.cellSx as any) ?? {},
                    ]}
                  >
                    {renderDataTableCell(col, { row, rowIndex })}
                  </TableCell>
                );
              })}
            </TableRow>
            );
          })}
          {footerTotals != null && (
            <TableTotalsFooterRow columns={columns} totals={footerTotals} useFirstColumnShell={false} />
          )}
        </TableBody>
      </Table>
    </CommonTable>
  );
}

/** Utilization-style sticky header: primary background, white label row */
export const utilizationPrimaryHeaderCellSx: SxProps<Theme> = {
  position: 'sticky',
  top: 0,
  zIndex: 10,
  backgroundColor: SEARCE_COLORS.primary,
  borderBottom: '1px solid #e0e0e0',
  padding: '6px 8px',
  /** Explicit `table-cell` keeps `<thead>` cells in one row (flex/block overrides break table layout). */
  display: 'table-cell',
  verticalAlign: 'middle',
  color: SEARCE_COLORS.white,
  fontSize: '0.75rem',
  '&:hover': {
    backgroundColor: alpha(SEARCE_COLORS.primary, 0.8),
  },
};

export default DataTable;
